/**
 * Date Utils
 * this file contains any cache related utils used for Date manipulation
 *
 * Naming Convention: camelCase
 * i.e. sayHello
 */
import {
  toHijri as toHijriDate,
  toGregorian as toGregorianDate,
} from "hijri-converter";
import parse from "date-fns/parse";
import format from "date-fns/format";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import getDate from "date-fns/getDate";
import subYears from "date-fns/subYears";
import getDaysInMonth from "date-fns/getDaysInMonth";
import isValid from "date-fns/isValid";
import parseISO from "date-fns/parseISO";
import type Locale from "date-fns/locale";
import _range from "lodash/range";
import { isHijri as isHijriDate } from "@tajawal/web-flights-sdk/lib/utils/pax.utils";
import _findIndex from "lodash/findIndex";
import _split from "lodash/split";
import _isNumber from "lodash/isNumber";
import enUS from "date-fns/locale/en-US";
import ar from "date-fns/locale/ar";
import { LANGUAGE } from "@tajawal/web-core-sdk-v2/lib/constants";
import {
  DATE_FORMATS,
  DATE_TYPES,
  INVALID_DATE_FORMAT,
  TRAVELLER_AGE_YEAR_MAP,
} from "@/constants/date.constants";
import { DateType, DateValue } from "@/types/date.types";
import { TFunction } from "i18next";
import { PaxType } from "@tajawal/web-flights-sdk/lib/types/common.types";
import type { Language } from "@tajawal/web-core-sdk-v2/lib/types/common.types";
import max from "date-fns/max";
import min from "date-fns/min";
import addYears from "date-fns/addYears";

export function getYearMonths({
  isHijri,
  t,
}: {
  isHijri: boolean;
  t: any;
}): string[] {
  return isHijri
    ? [
        t("Pax.Date_Muḥarram"),
        t("Pax.Date_Ṣafar"),
        t("Pax.Date_RabīʿAl-Awwal"),
        t("Pax.Date_RabīʿAth-Thānī"),
        t("Pax.Date_JumādáAl-Ūlá"),
        t("Pax.Date_JumādáAxl-Ākhirah"),
        t("Pax.Date_Rajab"),
        t("Pax.Date_Sha‘bān"),
        t("Pax.Date_Ramaḍān"),
        t("Pax.Date_Shawwāl"),
        t("Pax.Date_Dhū al-Qa‘dah"),
        t("Pax.Date_Dhū al-Ḥijjah"),
      ]
    : [
        t("Pax.Date_January"),
        t("Pax.Date_February"),
        t("Pax.Date_March"),
        t("Pax.Date_April"),
        t("Pax.Date_May"),
        t("Pax.Date_June"),
        t("Pax.Date_July"),
        t("Pax.Date_August"),
        t("Pax.Date_September"),
        t("Pax.Date_October"),
        t("Pax.Date_November"),
        t("Pax.Date_December"),
      ];
}

export function getMonthName(
  month: number,
  isHijri: boolean,
  t: TFunction,
): string {
  const months = getYearMonths({ isHijri, t });
  return months[month];
}

export function getMonthNumber(
  month: string,
  isHijri: boolean,
  t: TFunction,
): number {
  return _findIndex(
    getYearMonths({ isHijri, t }),
    (_month) => _month === month,
  );
}

export function toHijri(date: Date): Date {
  if (isHijriDate(date)) return new Date(date);
  // @ts-ignore remove once date-fns date is migrated to v2
  const [year, month, day] = Object.values(
    toHijriDate(getYear(date), getMonth(date) + 1, getDate(date)),
  );
  return new Date(year, month - 1, day);
}

export function toGregorian(date: Date): Date {
  const [year, month, day] = Object.values(
    toGregorianDate(getYear(date), getMonth(date) + 1, getDate(date)),
  );
  return new Date(year, month - 1, day);
}

/**
 * get last n years starting from current year
 */
export function getNYears({
  years,
  isHijri = false,
  backward = false,
  startDate = new Date(),
}: {
  years: number;
  isHijri: boolean;
  backward: boolean;
  startDate?: Date;
}): number[] {
  const currentYear = isHijri
    ? toHijri(startDate).getUTCFullYear()
    : startDate.getUTCFullYear();

  return _range(years).map((_, index) =>
    backward ? currentYear - index : currentYear + index,
  );
}

export function daysInMonth(
  { month: _month, year: _year }: DateValue,
  isHijri: boolean,
  t: TFunction,
): number {
  const month = _isNumber(_month) ? _month : getMonthNumber(_month, isHijri, t);
  if (isHijri) {
    return 30;
  }
  const year = !Number.isNaN(Number(_year)) ? _year : 4; // 4 is given to assume a leap year

  return !Number.isNaN(Number(month))
    ? getDaysInMonth(new Date(year, Number(month)))
    : 31;
}

export function getDateRanges({
  type,
  isHijri,
  t,
  isGCC,
  date,
}: {
  type: string;
  isHijri: boolean;
  t: any;
  isGCC: boolean;
  date: DateValue;
}): {
  day: number[];
  month: string[];
  year: number[];
} {
  const shouldBeHijri = isHijri && isGCC;
  const maxDays = daysInMonth(date, shouldBeHijri, t);
  const dayRange = _range(1, maxDays + 1);
  const monthRange = getYearMonths({ isHijri: shouldBeHijri, t });

  if (type === DATE_TYPES.ID_EXPIRY) {
    return {
      day: dayRange,
      month: monthRange,
      year: getNYears({
        // Maximum number of years for expiry + 1(current year)
        years: isGCC ? 21 : 11,
        isHijri: shouldBeHijri,
        backward: false,
      }),
    };
  }
  const years = getNYears({
    startDate: subYears(
      new Date(),
      TRAVELLER_AGE_YEAR_MAP[type]?.startYearOffset || 0,
    ),
    // Maximum allowed age + 1 year(current year)
    years: (TRAVELLER_AGE_YEAR_MAP[type]?.years || 110) + 1,
    isHijri: shouldBeHijri,
    backward: true,
  });
  return {
    day: dayRange,
    month: monthRange,
    year: years,
  };
}

export function getNewDate({
  day,
  month,
  year,
}: Omit<DateValue, "month"> & { month: number }): Date {
  return year && month >= 0 && day
    ? new Date(year, month, day)
    : new Date("Invalid date");
}

export function toDate(
  date: string | Date | number,
  parseFormat?: string,
): Date | number {
  return typeof date === "string"
    ? parse(date, parseFormat || "yyyy-MM-dd", new Date())
    : date;
}

export function getLocale(): Locale {
  let language: Language;
  if (typeof global?.config === "string") {
    language = JSON.parse(String(global?.config))?.language;
  } else if (typeof global?.config === "object") {
    language = global?.config?.language;
  }
  return language === LANGUAGE.EN ? enUS : ar;
}

export const formatDate = (
  date: Date | string | number,
  formatStr: string,
  parseFormat?: string,
): string => {
  const locale = getLocale();
  let _date = date;
  if (typeof date === "string") {
    const parsedDate = new Date(date.replace(/-/g, "/"));
    _date = isValid(parsedDate) ? parsedDate : parseISO(date);
  }
  if (!isValid(_date)) {
    return "";
  }

  return format(toDate(_date, parseFormat), formatStr, {
    locale,
  });
};

export function getDateInputValue(value: Date): string {
  if (!value || String(value) === INVALID_DATE_FORMAT) {
    return "";
  }
  return formatDate(value, DATE_FORMATS.DATE_INPUT_DATE_FORMAT);
}

export function getDateName(dateNumber: number): string {
  return dateNumber < 10 ? `0${dateNumber}` : `${dateNumber}`;
}

export function toCorrectCalendar(date: Date, isHijriCalendar: boolean): Date {
  const isDateHijri = isHijriDate(date);
  if (isDateHijri && !isHijriCalendar) return toGregorian(date);
  if (!isDateHijri && isHijriCalendar) return toHijri(date);
  return date;
}

export function getMinMaxDate(
  dateType: DateType,
  isHijri: boolean,
  isGCC: boolean,
): Date[] {
  let maxYear: number;
  let minYear: number;
  const presetDate = isHijri ? toHijri(new Date()) : new Date();
  if (dateType === DATE_TYPES.ID_EXPIRY) {
    const maxOffset = isGCC ? 20 : 10;
    minYear = getYear(subYears(presetDate, 0));
    maxYear = getYear(addYears(presetDate, maxOffset));
  } else {
    const minOffset = TRAVELLER_AGE_YEAR_MAP[dateType]?.years || 110;
    const maxOffset = TRAVELLER_AGE_YEAR_MAP[dateType]?.startYearOffset || 0;
    maxYear = getYear(subYears(presetDate, maxOffset));
    minYear = getYear(subYears(presetDate, minOffset));
  }

  const maxDate = new Date(maxYear, 11, isHijri ? 30 : 31);
  const minDate = new Date(minYear, 0, 1);
  return [maxDate, minDate];
}

export function getDateValueGroups(
  date: Date,
  isHijri: boolean,
  t: TFunction,
  dateType: PaxType,
  isGCC: boolean,
): DateValue {
  let dateObject = new Date(date);
  if (!date || String(date) === INVALID_DATE_FORMAT) {
    dateObject = subYears(
      new Date(),
      TRAVELLER_AGE_YEAR_MAP[dateType]?.startYearOffset || 0,
    );
  }
  dateObject = toCorrectCalendar(dateObject, isHijri);

  const [maxDate, minDate] = getMinMaxDate(dateType, isHijri, isGCC);

  dateObject = min([max([dateObject, minDate]), maxDate]);
  return {
    day: getDate(dateObject),
    month: getMonthName(getMonth(dateObject), isHijri, t),
    year: getYear(dateObject),
  };
}

export function getDateFromValues(
  dateValues: DateValue,
  isHijri: boolean,
  t: TFunction,
): Date {
  const day = dateValues?.day;
  const month = dateValues?.month as string;
  const year = dateValues?.year;

  return new Date(year, getMonthNumber(month, isHijri, t), day);
}

export function getValuesFromDate(date: Date) {
  const day = getDate(date);
  const month = getMonth(date);
  const year = getYear(date);

  return { day, month, year };
}

export function getFormattedTime(dateTime: Date, isAMFormat = false): string[] {
  const locale = getLocale();
  const formatStr = isAMFormat ? "hh:mm a" : "HH:mm";
  return _split(format(dateTime, formatStr, { locale }), " ");
}
